import { supabase } from '@/lib/supabase';
import { LEVEL_FILTERS } from '@/lib/logLevels';

export type CheckStatus = 'ok' | 'warn' | 'error' | 'unknown';

export interface DiagnosticCheck {
  id: string;
  label: string;
  status: CheckStatus;
  value: string;
  detail: string;
}

function statusForMs(ms: number): CheckStatus {
  if (ms > 2500) return 'error';
  if (ms > 900) return 'warn';
  return 'ok';
}

async function timeQuery(
  label: string,
  id: string,
  run: () => PromiseLike<{ error: { message: string } | null }>
): Promise<DiagnosticCheck> {
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    const { error } = await run();
    const elapsed =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
    const ms = Math.round(elapsed);
    if (error) {
      return {
        id,
        label,
        status: 'error',
        value: `${ms} ms`,
        detail: `La consulta falló: ${error.message}`,
      };
    }
    const status = statusForMs(ms);
    return {
      id,
      label,
      status,
      value: `${ms} ms`,
      detail:
        status === 'ok'
          ? 'Respuesta normal.'
          : status === 'warn'
            ? 'Está tardando más de lo esperado.'
            : 'Muy lento: revisar índices y tamaño de tablas.',
    };
  } catch (err) {
    return {
      id,
      label,
      status: 'error',
      value: '—',
      detail: err instanceof Error ? err.message : 'No se pudo medir la consulta.',
    };
  }
}

export async function runDatabaseChecks(): Promise<DiagnosticCheck[]> {
  return Promise.all([
    timeQuery('Productos', 'products', () =>
      supabase.from('products').select('id', { count: 'exact', head: true })
    ),
    timeQuery('Ventas', 'sales', () =>
      supabase.from('sales').select('id', { count: 'exact', head: true })
    ),
    timeQuery('Configuración de venta', 'sales_settings', () =>
      supabase.from('sales_settings').select('id', { count: 'exact', head: true })
    ),
    timeQuery('Inventario por sucursal', 'branch_inventory', () =>
      supabase.from('branch_inventory').select('id', { count: 'exact', head: true })
    ),
    timeQuery('Bitácora de actividad', 'activity_logs', () =>
      supabase.from('activity_logs').select('id', { count: 'exact', head: true })
    ),
  ]);
}

export interface ErrorSummary {
  errors: number;
  critical: number;
  total: number;
  check: DiagnosticCheck;
}

export async function runErrorCheck(days = 7): Promise<ErrorSummary> {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const started = performance.now();
  const [errorsRes, criticalRes, totalRes] = await Promise.all([
    supabase
      .from('activity_logs')
      .select('id', { count: 'exact', head: true })
      .or(LEVEL_FILTERS.error)
      .gte('created_at', since),
    supabase
      .from('activity_logs')
      .select('id', { count: 'exact', head: true })
      .or(LEVEL_FILTERS.critical)
      .gte('created_at', since),
    supabase
      .from('activity_logs')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', since),
  ]);
  const ms = Math.round(performance.now() - started);

  const failed = errorsRes.error || criticalRes.error;
  const errors = errorsRes.count ?? 0;
  const critical = criticalRes.count ?? 0;

  if (failed) {
    return {
      errors: 0,
      critical: 0,
      total: 0,
      check: {
        id: 'errors',
        label: 'Errores registrados',
        status: 'unknown',
        value: '—',
        detail: 'No se pudo leer la bitácora (permisos o tabla no disponible).',
      },
    };
  }

  const status: CheckStatus = critical > 0 ? 'error' : errors > 0 ? 'warn' : 'ok';
  return {
    errors,
    critical,
    total: totalRes.count ?? 0,
    check: {
      id: 'errors',
      label: `Incidencias últimos ${days} días`,
      status,
      value: `${errors + critical}`,
      detail:
        critical > 0
          ? `${critical} error(es) crítico(s) y ${errors} error(es) en ${days} días. Revisar la bitácora.`
          : errors > 0
            ? `${errors} error(es) registrados en ${days} días.`
            : `Sin errores registrados en ${days} días (${ms} ms de consulta).`,
    },
  };
}

export async function runBackupCheck(): Promise<DiagnosticCheck> {
  try {
    const { data, error } = await supabase
      .from('backups')
      .select('created_at, status, type, size_bytes')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      return {
        id: 'backup',
        label: 'Última copia de seguridad',
        status: 'unknown',
        value: '—',
        detail: 'No se pudo consultar el historial de copias.',
      };
    }

    const row = (data ?? [])[0] as
      | { created_at: string; status: string; type: string; size_bytes: number | null }
      | undefined;

    if (!row) {
      return {
        id: 'backup',
        label: 'Última copia de seguridad',
        status: 'error',
        value: 'Nunca',
        detail: 'No hay ninguna copia registrada. Crea una desde Copias de seguridad.',
      };
    }

    const ageDays = Math.floor(
      (Date.now() - new Date(row.created_at).getTime()) / 86400000
    );
    const status: CheckStatus = ageDays > 7 ? 'error' : ageDays > 2 ? 'warn' : 'ok';

    return {
      id: 'backup',
      label: 'Última copia de seguridad',
      status,
      value: ageDays === 0 ? 'Hoy' : `Hace ${ageDays} día(s)`,
      detail:
        ageDays > 2
          ? 'La copia es antigua. Se recomienda respaldar a diario.'
          : `Copia ${row.type} registrada con estado "${row.status}".`,
    };
  } catch {
    return {
      id: 'backup',
      label: 'Última copia de seguridad',
      status: 'unknown',
      value: '—',
      detail: 'No se pudo verificar el estado de las copias.',
    };
  }
}

export async function runStorageCheck(): Promise<DiagnosticCheck> {
  try {
    const { error } = await supabase.storage.from('products').list('', { limit: 1 });
    if (error) {
      return {
        id: 'storage',
        label: 'Almacenamiento de imágenes',
        status: 'error',
        value: 'Con error',
        detail: `No se pudo acceder al almacenamiento: ${error.message}`,
      };
    }
    return {
      id: 'storage',
      label: 'Almacenamiento de imágenes',
      status: 'ok',
      value: 'Disponible',
      detail: 'El almacenamiento de imágenes responde correctamente.',
    };
  } catch {
    return {
      id: 'storage',
      label: 'Almacenamiento de imágenes',
      status: 'unknown',
      value: '—',
      detail: 'No se pudo comprobar el almacenamiento.',
    };
  }
}

export function runDeviceChecks(): DiagnosticCheck[] {
  const checks: DiagnosticCheck[] = [];

  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const conn = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; downlink?: number };
    }
  ).connection;

  checks.push({
    id: 'network',
    label: 'Conexión del equipo',
    status: online ? 'ok' : 'error',
    value: online ? conn?.effectiveType?.toUpperCase() ?? 'En línea' : 'Sin conexión',
    detail: online
      ? `Latencia estimada: ${conn?.downlink ? `${conn.downlink} Mbps` : 'no disponible'}.`
      : 'El equipo perdió la conexión. Las ventas no se pueden registrar.',
  });

  // Uso de localStorage (borradores de venta, preferencias).
  try {
    let bytes = 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      bytes += (localStorage.getItem(key) ?? '').length + key.length;
    }
    const kb = Math.round(bytes / 1024);
    checks.push({
      id: 'localstorage',
      label: 'Datos locales del navegador',
      status: kb > 4500 ? 'error' : kb > 3000 ? 'warn' : 'ok',
      value: `${kb} KB`,
      detail:
        kb > 3000
          ? 'Hay muchos datos guardados localmente. Limpia borradores antiguos si el POS va lento.'
          : 'Uso normal (borradores y preferencias).',
    });
  } catch {
    checks.push({
      id: 'localstorage',
      label: 'Datos locales del navegador',
      status: 'unknown',
      value: '—',
      detail: 'El navegador bloqueó el almacenamiento local.',
    });
  }

  // Memoria del navegador (solo disponible en navegadores basados en Chromium).
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number };
  };
  if (perf.memory) {
    const usedMb = Math.round(perf.memory.usedJSHeapSize / 1048576);
    const limitMb = Math.round(perf.memory.jsHeapSizeLimit / 1048576);
    const ratio = perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit;
    checks.push({
      id: 'memory',
      label: 'Memoria del navegador',
      status: ratio > 0.85 ? 'error' : ratio > 0.6 ? 'warn' : 'ok',
      value: `${usedMb} / ${limitMb} MB`,
      detail:
        ratio > 0.6
          ? 'El consumo es alto. Recarga la pestaña al terminar la jornada para liberar memoria.'
          : 'Consumo saludable.',
    });
  } else {
    checks.push({
      id: 'memory',
      label: 'Memoria del navegador',
      status: 'unknown',
      value: 'No disponible',
      detail: 'Este navegador no expone el consumo de memoria.',
    });
  }

  return checks;
}

export function worstStatus(checks: DiagnosticCheck[]): CheckStatus {
  if (checks.some((c) => c.status === 'error')) return 'error';
  if (checks.some((c) => c.status === 'warn')) return 'warn';
  if (checks.some((c) => c.status === 'unknown')) return 'unknown';
  return 'ok';
}